import { FC } from "react"
import {
  CONFIG_ADMIN_INPUT_CLASS,
  ConfigAdminNumberInput,
  ConfigAdminPanelStackedField,
  ConfigAdminToggleRow
} from "../../../config-admin-form-primitives"
import { ConfigAdminPanel } from "../../../config-admin-panel"
import { SubscriptionConfigDetails } from "../../../../../../types"

interface Props {
  values: SubscriptionConfigDetails
  tab: string
  setDetailsField: (field: string, value: any) => void
}

const PROPERTY_TYPES = [
  {
    key: "single_family_home",
    label: "Single Family Home",
    description: "A standalone residential property"
  },
  {
    key: "townhouse",
    label: "Townhouse",
    description:
      "A multi-floor home that shares one or more walls with adjacent properties"
  },
  {
    key: "condo",
    label: "Condo",
    description: "A residential unit within a larger building or complex"
  },
  {
    key: "manufactured_home",
    label: "Manufactured Home",
    description: "A factory-built home constructed on a permanent chassis"
  },
  {
    key: "multi_unit",
    label: "Multi Unit",
    description:
      "A building that contains four (4) or more separate housing units"
  },
  {
    key: "home_three_units",
    label: "Home - Three Units",
    description: "A building that contains three (3) separate housing units"
  },
  {
    key: "home_two_units",
    label: "Home - Two Units",
    description: "A building that contains two (2) separate housing units"
  }
]

const MORTGAGE_TERMS = [
  { key: "30yr Fixed", label: "30yr Fixed" },
  { key: "20yr Fixed", label: "20yr Fixed" },
  { key: "15yr Fixed", label: "15yr Fixed" },
  { key: "10y/6m ARM", label: "10y/6m ARM" },
  { key: "7y/6m ARM", label: "7y/6m ARM" },
  { key: "5y/6m ARM", label: "5y/6m ARM" },
  { key: "3y/6m ARM", label: "3y/6m ARM" }
]

const SORT_OPTIONS = [
  { value: "relevance", label: "Relevance" },
  { value: "low_rate", label: "Low Rate" },
  { value: "low_apr", label: "Low APR" },
  { value: "low_monthly_payment", label: "Low Monthly Payment" },
  { value: "low_upfront_costs", label: "Low Upfront Costs" }
]

const ManageSubscriptionConfigFormContent: FC<Props> = ({
  values,
  tab,
  setDetailsField
}) => {
  const config = values.config || {}
  const fieldOverrides = config.field_overrides || {}
  const defaults = config.defaults || {}
  const dashboardConfig = config.dashboard_config || {}

  const updateDashboardConfig = (field: string, value: any) => {
    setDetailsField("dashboard_config", { ...dashboardConfig, [field]: value })
  }

  const getDashboardBoolean = (
    key: string,
    defaultValue: boolean,
    legacyKey?: string
  ): boolean => {
    const dashboardValue = dashboardConfig[key]
    if (typeof dashboardValue === "boolean") {
      return dashboardValue
    }

    if (legacyKey) {
      const legacyValue = config[legacyKey]
      if (typeof legacyValue === "boolean") {
        return legacyValue
      }
    }

    return defaultValue
  }

  const getDashboardString = (key: string, legacyKey?: string): string => {
    const dashboardValue = dashboardConfig[key]
    if (typeof dashboardValue === "string") {
      return dashboardValue
    }

    if (legacyKey) {
      const legacyValue = config[legacyKey]
      if (typeof legacyValue === "string") {
        return legacyValue
      }
    }

    return ""
  }

  const hasPropertyTypesConfig =
    (fieldOverrides.property_types?.length ?? 0) > 0
  const hasMortgageTermsConfig =
    (fieldOverrides.mortgage_terms?.length ?? 0) > 0

  const propertyTypesEnabled =
    fieldOverrides.property_types_enabled ?? hasPropertyTypesConfig
  const mortgageTermsEnabled =
    fieldOverrides.mortgage_terms_enabled ?? hasMortgageTermsConfig

  const updateFieldOverrides = (field: string, value: any) => {
    setDetailsField("field_overrides", { ...fieldOverrides, [field]: value })
  }

  const updateDefaults = (field: string, value: any) => {
    setDetailsField("defaults", { ...defaults, [field]: value })
  }

  const toggleListItem = (field: string, item: string) => {
    const current: string[] = fieldOverrides[field] || []
    updateFieldOverrides(
      field,
      current.includes(item)
        ? current.filter(v => v !== item)
        : [...current, item]
    )
  }

  return (
    <div className="space-y-6 pt-2">
      {tab === "field-overrides" && (
        <div className="space-y-6">
          <ConfigAdminPanel>
            <ConfigAdminToggleRow
              label="Property Types"
              description="Control which property type options appear on the subscription dashboard"
              enabled={propertyTypesEnabled}
              onChange={value => {
                setDetailsField("field_overrides", {
                  ...fieldOverrides,
                  property_types_enabled: value,
                  property_types: value ? fieldOverrides.property_types : []
                })
              }}
              last={false}
            />
            {propertyTypesEnabled &&
              PROPERTY_TYPES.map((pt, i) => (
                <ConfigAdminToggleRow
                  key={pt.key}
                  label={pt.label}
                  fieldKey={pt.key}
                  description={pt.description}
                  enabled={
                    fieldOverrides.property_types?.includes(pt.key) ?? true
                  }
                  onChange={() => toggleListItem("property_types", pt.key)}
                  last={i === PROPERTY_TYPES.length - 1}
                />
              ))}
          </ConfigAdminPanel>

          <ConfigAdminPanel>
            <ConfigAdminToggleRow
              label="Mortgage Terms"
              description="Select which mortgage programs to display on the subscription dashboard"
              enabled={mortgageTermsEnabled}
              onChange={value => {
                setDetailsField("field_overrides", {
                  ...fieldOverrides,
                  mortgage_terms_enabled: value,
                  mortgage_terms: value ? fieldOverrides.mortgage_terms : []
                })
              }}
              last={false}
            />
            {mortgageTermsEnabled &&
              MORTGAGE_TERMS.map((mt, i) => (
                <ConfigAdminToggleRow
                  key={mt.key}
                  label={mt.label}
                  enabled={
                    fieldOverrides.mortgage_terms?.includes(mt.key) ?? true
                  }
                  onChange={() => toggleListItem("mortgage_terms", mt.key)}
                  last={i === MORTGAGE_TERMS.length - 1}
                />
              ))}
          </ConfigAdminPanel>
        </div>
      )}

      {tab === "defaults" && (
        <ConfigAdminPanel>
          <ConfigAdminPanelStackedField
            label="Loan Type"
            fieldKey="loan_type"
            htmlFor="sub-loan-type"
          >
            <select
              id="sub-loan-type"
              value={defaults.loan_type || "conventional"}
              onChange={e => updateDefaults("loan_type", e.target.value)}
              className={CONFIG_ADMIN_INPUT_CLASS}
            >
              <option value="conventional">Conventional</option>
              <option value="fha">FHA</option>
              <option value="va">VA</option>
              <option value="usda">USDA</option>
              <option value="arm">ARM</option>
            </select>
          </ConfigAdminPanelStackedField>

          <ConfigAdminPanelStackedField
            label="Down Payment"
            fieldKey="down_payment"
            htmlFor="sub-down-payment"
          >
            <ConfigAdminNumberInput
              id="sub-down-payment"
              value={defaults.down_payment}
              onChange={v => updateDefaults("down_payment", v ?? 0)}
              parse="float"
            />
          </ConfigAdminPanelStackedField>

          <ConfigAdminPanelStackedField
            label="List Price"
            fieldKey="list_price"
            htmlFor="sub-list-price"
          >
            <ConfigAdminNumberInput
              id="sub-list-price"
              value={defaults.list_price}
              onChange={v => updateDefaults("list_price", v ?? 0)}
              parse="float"
            />
          </ConfigAdminPanelStackedField>

          <ConfigAdminPanelStackedField
            label="Lock Period"
            fieldKey="lock_period"
            htmlFor="sub-lock-period"
          >
            <select
              id="sub-lock-period"
              value={defaults.lock_period || 30}
              onChange={e =>
                updateDefaults("lock_period", parseInt(e.target.value))
              }
              className={CONFIG_ADMIN_INPUT_CLASS}
            >
              <option value="30">30 days</option>
              <option value="45">45 days</option>
              <option value="60">60 days</option>
            </select>
          </ConfigAdminPanelStackedField>

          <ConfigAdminPanelStackedField
            label="Property Type"
            fieldKey="property_type"
            htmlFor="sub-property-type"
          >
            <select
              id="sub-property-type"
              value={defaults.property_type || "single_family_home"}
              onChange={e => updateDefaults("property_type", e.target.value)}
              className={CONFIG_ADMIN_INPUT_CLASS}
            >
              <option value="single_family_home">Single Family Home</option>
              <option value="townhome">Townhome</option>
              <option value="condominium">Condominium</option>
              <option value="multi_tenant_home">Multi Tenant Home</option>
              <option value="manufactured_home">Manufactured Home</option>
            </select>
          </ConfigAdminPanelStackedField>

          <ConfigAdminPanelStackedField
            label="Residency Type"
            fieldKey="residency_type"
            htmlFor="sub-residency-type"
          >
            <select
              id="sub-residency-type"
              value={defaults.residency_type || "primary_home"}
              onChange={e => updateDefaults("residency_type", e.target.value)}
              className={CONFIG_ADMIN_INPUT_CLASS}
            >
              <option value="primary_home">Primary Home</option>
              <option value="second_home">Second Home</option>
              <option value="rental_home">Rental Home</option>
            </select>
          </ConfigAdminPanelStackedField>

          <ConfigAdminPanelStackedField
            label="Max Results"
            fieldKey="max_results"
            htmlFor="sub-max-results"
          >
            <ConfigAdminNumberInput
              id="sub-max-results"
              value={defaults.max_results}
              onChange={v => updateDefaults("max_results", v ?? 0)}
              parse="int"
              min={1}
            />
          </ConfigAdminPanelStackedField>

          <ConfigAdminPanelStackedField
            label="Min Price"
            fieldKey="min_price"
            hint="Optional"
            htmlFor="sub-min-price"
          >
            <ConfigAdminNumberInput
              id="sub-min-price"
              value={defaults.min_price}
              onChange={v => updateDefaults("min_price", v ?? 0)}
              parse="float"
              min={0}
            />
          </ConfigAdminPanelStackedField>

          <ConfigAdminPanelStackedField
            label="Frequency"
            fieldKey="frequency"
            htmlFor="sub-frequency"
            dividerBottom={false}
          >
            <select
              id="sub-frequency"
              value={defaults.frequency || "weekly"}
              onChange={e => updateDefaults("frequency", e.target.value)}
              className={CONFIG_ADMIN_INPUT_CLASS}
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </ConfigAdminPanelStackedField>
        </ConfigAdminPanel>
      )}

      {tab === "features" && (
        <ConfigAdminPanel>
          <ConfigAdminToggleRow
            label="Disable fees modal"
            fieldKey="fees_modal_disabled"
            description="On: modal hidden. Off: fees breakdown modal shown on the rate dashboard."
            enabled={getDashboardBoolean(
              "fees_modal_disabled",
              false,
              "fees_modal_disabled"
            )}
            onChange={value =>
              updateDashboardConfig("fees_modal_disabled", value)
            }
            last={false}
          />

          <ConfigAdminToggleRow
            label="AI Advisor"
            fieldKey="ai_advisor"
            description="Enables or disables the AI Advisor within the rate dashboard"
            enabled={getDashboardBoolean("ai_advisor", true, "ai_advisor")}
            onChange={value => updateDashboardConfig("ai_advisor", value)}
            last={false}
          />

          <ConfigAdminToggleRow
            label="Subscription CTA"
            fieldKey="subscription_cta_enabled"
            description="Show 'Subscribe to rate alerts' button below the rate results"
            enabled={getDashboardBoolean("subscription_cta_enabled", true)}
            onChange={value =>
              updateDashboardConfig("subscription_cta_enabled", value)
            }
            last={false}
          />

          <ConfigAdminToggleRow
            label="Enable NonQm"
            fieldKey="enable_nonqm"
            description="Enables the Non-QM loan type in the rate dashboard."
            enabled={getDashboardBoolean("enable_nonqm", false)}
            onChange={value => updateDashboardConfig("enable_nonqm", value)}
            last={false}
          />

          <ConfigAdminPanelStackedField
            label="Sort by default"
            fieldKey="sort_by_default"
            hint="Optional. Sets the default sort order of rate results."
            htmlFor="sub-sort-by-default"
            dividerBottom={false}
          >
            <select
              id="sub-sort-by-default"
              value={getDashboardString("sort_by_default", "sort_by_default")}
              onChange={e =>
                updateDashboardConfig(
                  "sort_by_default",
                  e.target.value || undefined
                )
              }
              className={CONFIG_ADMIN_INPUT_CLASS}
            >
              <option value="">Select...</option>
              {SORT_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </ConfigAdminPanelStackedField>
        </ConfigAdminPanel>
      )}
    </div>
  )
}

export default ManageSubscriptionConfigFormContent
